import { supabase } from '../../config/supabase';

/**
 * Fetch single job/assignment with all related data
 * @param {string} jobId - The job ID (can be UUID or unique_id)
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Job assignment with all relations
 */
export const getSingleAssignment = async (jobId, companyId) => {
    try {
        // Try to fetch by UUID first, then by unique_id if that fails
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*),
                job_notes(*),
                job_files(*),
                job_floors(*),
                job_floor_specs(*),
                job_action_logs(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Check if jobId is a UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (uuidRegex.test(jobId)) {
            query = query.eq('id', jobId);
        } else {
            query = query.eq('unique_id', jobId);
        }

        const { data, error } = await query.single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error fetching assignment:', error);
        throw error;
    }
};

/**
 * Add a note to a job
 * @param {string} jobId - The job ID
 * @param {string} content - Note content
 * @param {string} userId - User ID adding the note
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @returns {Promise<Object>} Created note
 */
export const addJobNote = async (jobId, content, userId, userName, userRole) => {
    try {
        const { data, error } = await supabase
            .from('job_notes')
            .insert({
                job_id: jobId,
                content: content,
                added_by_user_id: userId,
                added_by_name: userName,
                added_by_role: userRole
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error adding note:', error);
        throw error;
    }
};

/**
 * Update a job note
 * @param {string} noteId - The note ID
 * @param {string} content - Updated content
 * @param {string} userId - User ID editing the note
 * @returns {Promise<Object>} Updated note
 */
export const updateJobNote = async (noteId, content, userId) => {
    try {
        const { data, error } = await supabase
            .from('job_notes')
            .update({
                content: content,
                is_edited: true,
                edited_at: new Date().toISOString()
            })
            .eq('id', noteId)
            .eq('added_by_user_id', userId) // Ensure user can only edit their own notes
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating note:', error);
        throw error;
    }
};

/**
 * Delete a job note
 * @param {string} noteId - The note ID
 * @param {string} userId - User ID deleting the note
 * @returns {Promise<void>}
 */
export const deleteJobNote = async (noteId, userId) => {
    try {
        const { error } = await supabase
            .from('job_notes')
            .delete()
            .eq('id', noteId)
            .eq('added_by_user_id', userId); // Ensure user can only delete their own notes

        if (error) throw error;
    } catch (error) {
        console.error('Error deleting note:', error);
        throw error;
    }
};

/**
 * Get available designers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of designers
 */
export const getAvailableDesigners = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'designer')
            .eq('status', 'active')
            .order('full_name');

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching designers:', error);
        throw error;
    }
};

/**
 * Get available estimators for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of estimators
 */
export const getAvailableEstimators = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'estimator')
            .eq('status', 'active')
            .order('full_name');

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching estimators:', error);
        throw error;
    }
};

/**
 * Get available drivers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of drivers
 */
export const getAvailableDrivers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'driver')
            .eq('status', 'active')
            .order('full_name');

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching drivers:', error);
        throw error;
    }
};

/**
 * Assign a designer to a quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor)
 * @param {string} designerId - Designer user ID
 * @param {string} designerName - Designer name
 * @returns {Promise<Object>} Created designer assignment
 */
export const assignDesigner = async (jobId, quoteType, designerId, designerName) => {
    try {
        const { data, error } = await supabase
            .from('job_designers')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                user_id: designerId,
                name: designerName,
                is_started: false,
                is_completed: false
            })
            .select()
            .single();


        // Update job_quotes design status to "in_progress"
        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ design_status: 'in_progress' })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (quoteError) throw quoteError;
        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning designer:', error);
        throw error;
    }
};

/**
 * Remove a designer from a quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {string} designerId - Designer user ID
 * @returns {Promise<void>}
 */
export const removeDesigner = async (jobId, quoteType, designerId) => {
    try {
        // Check if design_status is completed
        const { data: quoteData, error: fetchError } = await supabase
            .from('job_quotes')
            .select('design_status')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .single();

        if (fetchError) throw fetchError;

        // If design is completed, don't remove the designer
        if (quoteData.design_status === 'completed') {
            throw new Error('Cannot remove designer: design is already completed');
        }

        // Remove designer
        const { error } = await supabase
            .from('job_designers')
            .delete()
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .eq('user_id', designerId);

        if (error) throw error;

        // Update quote status to waiting_assignment (only if not completed)
        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ design_status: 'waiting_assignment' })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .neq('design_status', 'completed');

        if (quoteError) throw quoteError;
    } catch (error) {
        console.error('Error removing designer:', error);
        throw error;
    }
};

/**
 * Assign an estimator to a job
 * @param {string} jobId - The job ID
 * @param {string} estimatorId - Estimator user ID
 * @param {string} estimatorName - Estimator name
 * @returns {Promise<Object>} Created estimator assignment
 */
export const assignEstimator = async (jobId, estimatorId, estimatorName) => {
    try {
        const { data, error } = await supabase
            .from('job_estimators')
            .insert({
                job_id: jobId,
                user_id: estimatorId,
                name: estimatorName,
                is_started: false,
                is_completed: false
            })
            .select()
            .single();

        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ estimation_status: 'in_progress' })
            .eq('job_id', jobId)

        if (quoteError) throw quoteError;

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error assigning estimator:', error);
        throw error;
    }
};

/**
 * Remove an estimator from a job
 * @param {string} jobId - The job ID
 * @returns {Promise<void>}
 */
export const removeEstimator = async (jobId) => {
    try {
        const { error } = await supabase
            .from('job_estimators')
            .delete()
            .eq('job_id', jobId);

        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ estimation_status: 'waiting_assignment' })
            .eq('job_id', jobId)

        if (quoteError) throw quoteError;

        if (error) throw error;
    } catch (error) {
        console.error('Error removing estimator:', error);
        throw error;
    }
};

/**
 * Assign a driver to a delivery for a specific quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor/timber)
 * @param {string} driverId - Driver user ID
 * @param {string} driverName - Driver name
 * @returns {Promise<Object>} Updated or created delivery record
 */
export const assignDriver = async (jobId, quoteType, driverId, driverName) => {
    try {
        // First, check if a delivery record exists for this job and quote type
        const { data: existingDelivery, error: fetchError } = await supabase
            .from('job_deliveries')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
            throw fetchError;
        }

        let result;

        if (existingDelivery) {
            // Update existing delivery record
            const { data, error } = await supabase
                .from('job_deliveries')
                .update({
                    assigned_driver_id: driverId,
                    assigned_driver_name: driverName,
                    assigned_at: new Date().toISOString(),
                    delivery_status: 'scheduled'
                })
                .eq('id', existingDelivery.id)
                .select()
                .single();

            if (error) throw error;
            result = data;
        } else {
            // Create new delivery record
            const { data, error } = await supabase
                .from('job_deliveries')
                .insert({
                    job_id: jobId,
                    quote_type: quoteType,
                    is_required: true,
                    assigned_driver_id: driverId,
                    assigned_driver_name: driverName,
                    assigned_at: new Date().toISOString(),
                    delivery_status: 'scheduled'
                })
                .select()
                .single();

            if (error) throw error;
            result = data;
        }

        return result;
    } catch (error) {
        console.error('Error assigning driver:', error);
        throw error;
    }
};

/**
 * Remove a driver from a delivery
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @returns {Promise<void>}
 */
export const removeDriver = async (jobId, quoteType) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: null,
                assigned_driver_name: null,
                assigned_at: null,
                assigned_by_user_id: null,
                assigned_by_name: null,
                delivery_status: 'not_scheduled'
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error removing driver:', error);
        throw error;
    }
};

/**
 * Confirm package receipt (driver action)
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @returns {Promise<Object>} Updated delivery record
 */
export const confirmPackageReceipt = async (jobId, quoteType) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                delivery_status: 'in_transit'
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error confirming package receipt:', error);
        throw error;
    }
};

/**
 * Upload delivery photo to public bucket
 * @param {string} jobId - The job ID
 * @param {File} file - Photo file to upload
 * @param {string} quoteType - Quote type (roof/wall/floor/timber)
 * @param {string} userId - User ID uploading
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @returns {Promise<Object>} Uploaded file record
 */
export const uploadDeliveryPhoto = async (jobId, file, quoteType, userId, userName, userRole, companySlug) => {
    try {
        // Upload to public storage bucket
        const filename = `${companySlug}/${jobId}/${Date.now()}-${file.name}`;
        const { data: storageData, error: storageError } = await supabase.storage
            .from('delivery-uploads') // Public bucket for delivery photos
            .upload(filename, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (storageError) throw storageError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('delivery-photos')
            .getPublicUrl(filename);

        // Create file record
        const { data, error } = await supabase
            .from('job_files')
            .insert({
                job_id: jobId,
                filename: filename,
                original_name: file.name,
                url: publicUrl,
                uploaded_by_user_id: userId,
                uploaded_by_name: userName,
                uploaded_by_role: userRole,
                file_type: 'delivery',
                quote_type: quoteType
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error uploading delivery photo:', error);
        throw error;
    }
};

/**
 * Complete delivery (driver action)
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {string} driverId - Driver user ID
 * @param {string} driverName - Driver name
 * @param {string} deliveryNotes - Optional delivery notes
 * @returns {Promise<Object>} Updated delivery record
 */
export const completeDelivery = async (jobId, quoteType, driverId, driverName, deliveryNotes = '') => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                delivery_status: 'delivered',
                delivered_at: new Date().toISOString(),
                delivered_by_user_id: driverId,
                delivered_by_name: driverName,
                delivery_notes: deliveryNotes
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error completing delivery:', error);
        throw error;
    }
};


/**
 * Complete a design for a specific quote type with file uploads
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor)
 * @param {string} designerId - Designer user ID
 * @param {string} designerName - Designer name
 * @param {string} completionNote - Completion note
 * @param {Array<File>} files - Array of files to upload
 * @param {string} companySlug - Company slug
 * @returns {Promise<Object>} Updated designer record with uploaded files
 */
export const completeDesign = async (jobId, quoteType, designerId, designerName, completionNote, files, companySlug) => {
    try {
        // Upload all files first
        const uploadedFiles = [];

        for (const file of files) {
            try {
                const fileRecord = await uploadJobFile(
                    jobId,
                    file,
                    'design',
                    quoteType,
                    designerId,
                    designerName,
                    'designer',
                    companySlug
                );
                uploadedFiles.push(fileRecord);
            } catch (uploadError) {
                console.error(`Error uploading file ${file.name}:`, uploadError);
                // Continue with other files even if one fails
            }
        }

        // Update designer record to mark as completed
        const { data, error } = await supabase
            .from('job_designers')
            .update({
                is_completed: true,
                completed_at: new Date().toISOString(),
                finishing_note: completionNote
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .eq('user_id', designerId)
            .select()
            .single();

        if (error) throw error;

        // Update job_quotes design_status
        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({
                design_status: 'completed'
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (quoteError) console.error('Error updating quote status:', quoteError);

        return {
            ...data,
            uploaded_files: uploadedFiles
        };
    } catch (error) {
        console.error('Error completing design:', error);
        throw error;
    }
};

/**
 * Complete estimation for a job with file uploads
 * @param {string} jobId - The job ID
 * @param {string} estimatorId - Estimator user ID
 * @param {string} estimatorName - Estimator name
 * @param {string} completionNote - Completion note
 * @param {Array<File>} files - Array of files to upload
 * @param {string} companySlug - Company slug
 * @returns {Promise<Object>} Updated estimator record with uploaded files
 */
export const completeEstimation = async (jobId, estimatorId, estimatorName, completionNote, files, companySlug) => {
    try {
        // Upload all files first
        const uploadedFiles = [];

        for (const file of files) {
            try {
                const fileRecord = await uploadJobFile(
                    jobId,
                    file,
                    'estimation',
                    null, // No quote type for estimation files
                    estimatorId,
                    estimatorName,
                    'estimator',
                    companySlug
                );
                uploadedFiles.push(fileRecord);
            } catch (uploadError) {
                console.error(`Error uploading file ${file.name}:`, uploadError);
                // Continue with other files even if one fails
            }
        }

        // Update estimator record to mark as completed
        const { data, error } = await supabase
            .from('job_estimators')
            .update({
                is_completed: true,
                completed_at: new Date().toISOString(),
                finishing_note: completionNote
            })
            .eq('job_id', jobId)
            .eq('user_id', estimatorId)
            .select()
            .single();

        if (error) throw error;

        return {
            ...data,
            uploaded_files: uploadedFiles
        };
    } catch (error) {
        console.error('Error completing estimation:', error);
        throw error;
    }
};

/**
 * Upload job files with proper path structure
 * @param {string} jobId - The job ID
 * @param {File} file - File to upload
 * @param {string} fileType - Type of file (initial/design/estimation/delivery)
 * @param {string} quoteType - Quote type (optional, null for estimation)
 * @param {string} userId - User ID uploading
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @param {string} companySlug - Company slug for path
 * @returns {Promise<Object>} Uploaded file record
 */
export const uploadJobFile = async (jobId, file, fileType, quoteType, userId, userName, userRole, companySlug) => {
    try {
        // Create proper path structure: companySlug/jobId/type-timestamp-filename
        const timestamp = Date.now();
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filename = `${companySlug}/${jobId}/${fileType}-${timestamp}-${sanitizedFileName}`;

        // Upload to storage
        const { data: storageData, error: storageError } = await supabase.storage
            .from('job-files')
            .upload(filename, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (storageError) throw storageError;

        // Create file record in database
        const { data, error } = await supabase
            .from('job_files')
            .insert({
                job_id: jobId,
                filename: filename,
                original_name: file.name,
                url: filename, // Store the path, not public URL since it's a private bucket
                uploaded_by_user_id: userId,
                uploaded_by_name: userName,
                uploaded_by_role: userRole,
                file_type: fileType,
                quote_type: quoteType
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

/**
 * Subscribe to single job updates
 * @param {string} jobId - The job ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
export const subscribeToJobUpdates = (jobId, callback) => {
    const channel = supabase
        .channel(`job-${jobId}-changes`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `id=eq.${jobId}`
            },
            callback
        )
        .subscribe();

    return channel;
};